import { useState, useCallback } from 'react';
import { toast } from '../components/toast';
import { getAllEmployees, getEmployeeDashboardStats } from '../services/api';

export const useEmployeeDashboardOperations = () => {
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [attendanceStats, setAttendanceStats] = useState({
    total_records: 0,
    present_count: 0,
    absent_count: 0,
    on_leave_count: 0,
    average_attendance: 0,
    monthly_trend: [],
  });

  const [leaveStats, setLeaveStats] = useState({
    total_leaves: 0,
    pending_leaves: 0,
    approved_leaves: 0,
    rejected_leaves: 0,
  });

  const [payrollStats, setPayrollStats] = useState({
    total_payrolls: 0,
    total_gross_pay: 0,
    total_net_pay: 0,
    current_month_payrolls: 0,
  });

  const [calendarDays, setCalendarDays] = useState([]);

  const [filterOptions, setFilterOptions] = useState({
    employees: [],
  });
  const [employeesLoaded, setEmployeesLoaded] = useState(false);

  const resetDashboardData = useCallback(() => {
    setAttendanceStats({
      total_records: 0,
      present_count: 0,
      absent_count: 0,
      on_leave_count: 0,
      average_attendance: 0,
      monthly_trend: [],
    });

    setLeaveStats({
      total_leaves: 0,
      pending_leaves: 0,
      approved_leaves: 0,
      rejected_leaves: 0,
    });

    setPayrollStats({
      total_payrolls: 0,
      total_gross_pay: 0,
      total_net_pay: 0,
      current_month_payrolls: 0,
    });
    setCalendarDays([]);
  }, []);

  const loadFilterOptions = useCallback(async () => {
    if (employeesLoaded) {
      return;
    }

    setFiltersLoading(true);
    try {
      const employeesResponse = await getAllEmployees();
      const employees = Array.isArray(employeesResponse.data?.results) ? employeesResponse.data.results : [];

      setFilterOptions({
        employees: employees.map((employee) => ({
          id: employee.id,
          full_name: employee.full_name,
          employee_id: employee.employee_id,
        })),
      });
      setEmployeesLoaded(true);
    } catch (error) {
      console.error('Error loading employee dashboard employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setFiltersLoading(false);
    }
  }, [employeesLoaded]);

  const loadDashboardData = useCallback(
    async (
      employeeFilter = '',
      departmentFilter = 'All',
      designationFilter = 'All',
      statusFilter = 'All',
      startDate = null,
      endDate = null
    ) => {
      try {
        if (!employeeFilter) {
          setDashboardLoading(false);
          resetDashboardData();
          return;
        }

        setDashboardLoading(true);
        const statsResponse = await getEmployeeDashboardStats(
          employeeFilter,
          departmentFilter,
          designationFilter,
          statusFilter,
          startDate,
          endDate
        );
        const statsData = statsResponse.data;

        setAttendanceStats({
          total_records: statsData.attendanceStats?.totalRecords || 0,
          present_count: statsData.attendanceStats?.presentCount || 0,
          absent_count: statsData.attendanceStats?.absentCount || 0,
          on_leave_count: statsData.attendanceStats?.onLeaveCount || 0,
          average_attendance: statsData.attendanceStats?.averageAttendance || 0,
          monthly_trend: statsData.attendanceStats?.monthlyTrend || [],
        });

        setLeaveStats({
          total_leaves:
            (statsData.leaveStats?.pendingLeaves || 0) +
            (statsData.leaveStats?.approvedLeaves || 0) +
            (statsData.leaveStats?.rejectedLeaves || 0),
          pending_leaves: statsData.leaveStats?.pendingLeaves || 0,
          approved_leaves: statsData.leaveStats?.approvedLeaves || 0,
          rejected_leaves: statsData.leaveStats?.rejectedLeaves || 0,
        });

        setPayrollStats({
          total_payrolls: statsData.payrollStats?.payrollCount || 0,
          total_gross_pay: statsData.payrollStats?.monthlyGrossPayroll || 0,
          total_net_pay: statsData.payrollStats?.monthlyNetPayroll || 0,
          current_month_payrolls: statsData.payrollStats?.payrollCount || 0,
        });

        setCalendarDays(Array.isArray(statsData.calendarDays) ? statsData.calendarDays : []);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setDashboardLoading(false);
      }
    },
    [resetDashboardData]
  );

  const refreshStats = useCallback(async () => {
    try {
      await loadDashboardData();
    } catch (error) {
      console.error('Error refreshing stats:', error);
    }
  }, [loadDashboardData]);

  return {
    isLoading: dashboardLoading,
    filtersLoading,
    dashboardLoading,
    attendanceStats,
    leaveStats,
    payrollStats,
    calendarDays,
    filterOptions,
    loadFilterOptions,
    loadDashboardData,
    resetDashboardData,
    refreshStats,
  };
};
